import { Trash, XIcon } from '@/shared/ui/icons'
import {
	TransparentButton,
	SideBarContainer,
	SideBarHeader,
	SideBarTitle,
	SideBarSubtitle,
	Separator,
	ChatHistoryList,
	ChatHistoryItem,
	ChatHistoryItemTitle,
	ChatHistoryTitle
} from './styles'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { useDeleteThreadMutation, useGetThreadsQuery } from '@/features/chat-bot/api/threads'
import { Button, AlertDialog, Tooltip } from '@/shared/ui'
import { PlusIcon } from '@/features/user-profile/inviteUsers/ui/InviteUsersButton/styles'
import { toast } from 'react-toastify'
import { useState } from 'react'
import { useAppSelector } from '@/shared/lib/hooks'

interface RightSidebarProps {
	open: boolean
	onClose: () => void
	activeSubscription: boolean
}

export const RightSidebar = ({ open, onClose, activeSubscription }: RightSidebarProps) => {
	const navigate = useNavigate()
	const searchParam = useSearchParams()
	const currentThreadId = searchParam[0]?.get('threadId')


	const { data: threadsData, isLoading: isLoadingThreads } =
		useGetThreadsQuery(activeSubscription ? undefined : skipToken)

	const isChatStreaming = useAppSelector((state) => state.layout.isChatStreaming)

	const [deleteThread] = useDeleteThreadMutation()
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [threadIdToDelete, setThreadIdToDelete] = useState<string | null>(null)

	const handleDeleteClick = (threadId: string, e: React.MouseEvent) => {
		e.stopPropagation()
		setThreadIdToDelete(threadId)
		setIsDeleteDialogOpen(true)
	}

	const handleDeleteThread = () => {
		if (threadIdToDelete === null) return

		deleteThread({ threadId: threadIdToDelete })
			.unwrap()
			.then(() => {
				toast.success('Thread deleted successfully.')
				if (threadIdToDelete === currentThreadId) {
					navigate(`/ai-chatbot`)
				}
				setIsDeleteDialogOpen(false)
				setThreadIdToDelete(null)
			})
			.catch(() => {
				toast.error('Failed to delete thread.')
				setIsDeleteDialogOpen(false)
				setThreadIdToDelete(null)
			})
	}

	return (
		<>
			<SideBarContainer $open={open}>
				<SideBarHeader>
					<div>
						<SideBarTitle>Chat History</SideBarTitle>
						<SideBarSubtitle>View your chat history</SideBarSubtitle>
					</div>
					<TransparentButton
						variant='secondary'
						startIcon={<XIcon color='white' />}
						onClick={() => {
							onClose()
						}}
					/>
				</SideBarHeader>
				<Separator $color={'#262626'} />

				<div style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
					{isChatStreaming ? (
						<Tooltip content='Stop streaming first.'>
							<div>
								<Button
									variant='primary'
									startIcon={<PlusIcon />}
									onClick={() => { }}
									fullWidth
									label='New Chat'
									disabled={true}
								/>
							</div>
						</Tooltip>
					) : (
						<Button
							variant='primary'
							startIcon={<PlusIcon />}
							onClick={() => {
								navigate(`/ai-chatbot`)
							}}
							fullWidth
							label='New Chat'
						/>
					)}
				</div>

				<ChatHistoryList>
					{isLoadingThreads && (
						<ChatHistoryTitle>Loading...</ChatHistoryTitle>
					)}
					{!isLoadingThreads && threadsData?.threads.length === 0 && (
						<ChatHistoryTitle>No chat history found. Start a new chat to get started.</ChatHistoryTitle>
					)}
					{!isLoadingThreads &&
						threadsData?.threads.map((thread) => {
							const isCurrentThread = currentThreadId === thread.id.toString()
							const shouldDisable = isChatStreaming && !isCurrentThread

							return shouldDisable ? (
								<Tooltip key={thread.id} content='Stop streaming first.'>
									<ChatHistoryItem
										onClick={() => { }}
										className={isCurrentThread ? 'selected' : ''}
										style={{ cursor: 'not-allowed', opacity: 0.5 }}
									>
										<ChatHistoryItemTitle>
											{thread.title || 'Chat ' + thread.id.toString()}
										</ChatHistoryItemTitle>
										<Trash
											width={16}
											height={16}
											onClick={(e) => {
												e.stopPropagation()
											}}
											style={{ cursor: 'not-allowed', opacity: 0.5 }}
										/>
									</ChatHistoryItem>
								</Tooltip>
							) : (
								<ChatHistoryItem
									key={thread.id}
									onClick={() => {
										navigate(`/ai-chatbot?threadId=${thread.id}`)
									}}
									className={isCurrentThread ? 'selected' : ''}
								>
									<ChatHistoryItemTitle>
										{thread.title || 'Chat ' + thread.id.toString()}
									</ChatHistoryItemTitle>
									<Trash
										width={16}
										height={16}
										onClick={(e) => {
											handleDeleteClick(thread.id, e)
										}}
									/>
								</ChatHistoryItem>
							)
						})}
				</ChatHistoryList>
			</SideBarContainer>

			<AlertDialog
				open={isDeleteDialogOpen}
				openChange={setIsDeleteDialogOpen}
				title='Delete Thread'
				description='Are you sure you want to delete this thread? This action is permanent and cannot be undone.'
				confirmText='Delete'
				cancelText='Cancel'
				confirmColor='error'
				confirmAction={handleDeleteThread}
			/>
		</>
	)
}
